import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getCampaign, getCampaignEmails, sendCampaign, pauseCampaign,
  resumeCampaign, stopCampaign, checkReplies, getEmailChat,
  sendReply, socket
} from '../api';
import toast from 'react-hot-toast';
import {
  Send, Pause, Play, StopCircle, X, CheckCircle, Clock, AlertCircle, XCircle,
  MessageSquare, ArrowLeft, RefreshCw, Mail, MailOpen, Paperclip,
  Search as SearchIcon, MailCheck
} from 'lucide-react';

export default function CampaignDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [emails, setEmails] = useState([]);
  const [tab, setTab] = useState('overview');
  const [filter, setFilter] = useState('');
  const [chatModal, setChatModal] = useState(null);
  const [chatData, setChatData] = useState({ emailLog: null, replies: [] });
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);

  const loadCampaign = useCallback(async () => {
    try { setCampaign((await getCampaign(id)).data); } catch (e) { nav('/campaigns'); }
  }, [id, nav]);

  const loadEmails = useCallback(async () => {
    try { setEmails((await getCampaignEmails(id, filter || undefined)).data); } catch (e) { }
  }, [id, filter]);

  useEffect(() => { loadCampaign(); }, [loadCampaign]);
  useEffect(() => { if (tab === 'emails' || tab === 'replies') loadEmails(); }, [tab, loadEmails]);

  // Socket.IO real-time updates
  useEffect(() => {
    const onUpdate = (d) => { if (d.campaignId === id) { loadCampaign(); if (tab === 'emails') loadEmails(); } };
    const onComplete = (d) => { if (d.campaignId === id) { loadCampaign(); toast.success('Campaign completed!'); } };
    const onReply = (d) => { if (d.campaignId === id) { loadCampaign(); loadEmails(); toast('New reply from ' + d.from, { icon: '💬' }); } };

    socket.on('campaign-update', onUpdate);
    socket.on('campaign-complete', onComplete);
    socket.on('new-reply', onReply);
    return () => { socket.off('campaign-update', onUpdate); socket.off('campaign-complete', onComplete); socket.off('new-reply', onReply); };
  }, [id, tab, loadCampaign, loadEmails]);

  // Auto-refresh
  useEffect(() => {
    const iv = setInterval(() => { loadCampaign(); if (tab === 'emails') loadEmails(); }, 5000);
    return () => clearInterval(iv);
  }, [loadCampaign, loadEmails, tab]);

  const handleSend = async () => {
    setSending(true);
    try {
      const r = (await sendCampaign(id)).data;
      toast.success(`Sending to ${r.totalRecipients} contacts!`);
      loadCampaign();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to send'); }
    setSending(false);
  };

  const handlePause = async () => { try { await pauseCampaign(id); toast.success('Paused'); loadCampaign(); } catch (e) { toast.error('Failed'); } };
  const handleResume = async () => { try { await resumeCampaign(id); toast.success('Resumed'); loadCampaign(); } catch (e) { toast.error('Failed'); } };
  const handleStop = async () => { try { await stopCampaign(id); toast.success('Stopped'); loadCampaign(); } catch (e) { toast.error('Failed'); } };

  const handleCheckReplies = async () => {
    setChecking(true);
    try {
      const r = (await checkReplies(id)).data;
      if (r.newReplies > 0) toast.success(`Found ${r.newReplies} new replies!`);
      else toast('No new replies found', { icon: '📭' });
      loadCampaign(); loadEmails();
    } catch (e) { toast.error(e.response?.data?.error || 'IMAP check failed'); }
    setChecking(false);
  };

  const openChat = async (emailId) => {
    try {
      const r = (await getEmailChat(emailId)).data;
      setChatData(r);
      setChatModal(emailId);
    } catch (e) { toast.error('Failed to load chat'); }
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    try {
      await sendReply(chatModal, replyText);
      toast.success('Reply sent!');
      setReplyText('');
      openChat(chatModal); // reload
      loadCampaign();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };

  if (!campaign) return <div className="pg-body" style={{ color: 'var(--txtm)' }}>Loading...</div>;

  const s = campaign.stats || { total: 0, pending: 0, sent: 0, delivered: 0, failed: 0, replied: 0 };
  const progress = s.total > 0 ? ((s.delivered + s.failed + s.replied) / s.total * 100) : 0;
  const tpl = campaign.templateId;
  const smtp = campaign.smtpConfigId;

  return (<>
    <div className="pg-hdr">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button className="btn btn-g btn-sm" onClick={() => nav('/campaigns')}><ArrowLeft size={15} /></button>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><h2>{campaign.name}</h2><span className={`badge b-${campaign.status}`}>{campaign.status}</span></div>
          <p>Template: {tpl?.name || '—'} • SMTP: {smtp?.name || '—'} ({smtp?.fromEmail || '—'})</p>
        </div>
      </div>
      <div className="pg-acts">
        {campaign.status === 'draft' && <button className="btn btn-p" onClick={handleSend} disabled={sending}><Send size={15} /> {sending ? 'Launching...' : 'Launch Campaign'}</button>}
        {campaign.status === 'running' && <>
          <button className="btn btn-s" onClick={handlePause}><Pause size={15} /> Pause</button>
          <button className="btn btn-d" onClick={handleStop}><StopCircle size={15} /> Stop</button>
        </>}
        {campaign.status === 'paused' && <button className="btn btn-ok" onClick={handleResume}><Play size={15} /> Resume</button>}
        <button className="btn btn-s" onClick={handleCheckReplies} disabled={checking}>
          <MailCheck size={15} /> {checking ? 'Checking...' : 'Check Replies (IMAP)'}
        </button>
        <button className="btn btn-g" onClick={() => { loadCampaign(); loadEmails(); }}><RefreshCw size={15} /></button>
      </div>
    </div>

    <div className="pg-body">
      {/* Stats Row */}
      <div className="stats">
        <SC icon={<Mail size={18} />} label="Total" val={s.total} cls="si-i" />
        <SC icon={<Send size={18} />} label="Sent" val={(s.sent||0)+(s.delivered||0)+(s.replied||0)+(s.bounced||0)} cls="si-i" />
        <SC icon={<CheckCircle size={18} />} label="Delivered" val={s.delivered} cls="si-ok" />
        <SC icon={<XCircle size={18} />} label="Failed" val={s.failed||0} cls="si-err" />
        <SC icon={<AlertCircle size={18} />} label="Bounced" val={s.bounced||0} cls="si-err" />
        <SC icon={<MessageSquare size={18} />} label="Replied" val={s.replied} cls="si-r" />
      </div>

      {/* Progress Bar */}
      {s.total > 0 && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--txt2)' }}>SENDING PROGRESS</span>
            <span style={{ fontSize: 11.5, fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{Math.round(progress)}%</span>
          </div>
          <div className="progress"><div className="progress-fill" style={{ width: `${progress}%` }} /></div>
          {campaign.status === 'running' && (
            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ok)', animation: 'pulse 1.5s infinite' }} />
              <span style={{ fontSize: 10.5, color: 'var(--ok)' }}>Live — sending in progress</span>
            </div>
          )}
          <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.3}}`}</style>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 14, borderBottom: '1px solid var(--bdr)' }}>
        {['overview', 'emails', 'replies'].map(t => (
          <button key={t} className="btn btn-g btn-sm"
            style={{ borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent', borderRadius: 0, color: tab === t ? 'var(--accent)' : 'var(--txtm)' }}
            onClick={() => setTab(t)}>
            {t === 'overview' ? 'Overview' : t === 'emails' ? 'Email Log' : 'Replies & Chat'}
          </button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {tab === 'overview' && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: 12 }}>Campaign Configuration</div>
          <div style={{ display: 'grid', gap: 14 }}>
            <Row label="SMTP Account" val={`${smtp?.name || '—'} — ${smtp?.fromName || ''} <${smtp?.fromEmail || ''}>`} />
            <Row label="Template" val={tpl?.name || '—'} />
            <Row label="Subject" val={tpl?.subject || '—'} />
            <Row label="Target Tags" val={campaign.contactTags?.length > 0 ? campaign.contactTags.map((t, i) => <span className="tag" key={i}>{t}</span>) : 'None'} />
            <Row label="Delay" val={`${campaign.delayBetweenEmails}ms between emails`} />
            {tpl?.attachments?.length > 0 && <Row label="Attachments" val={tpl.attachments.map((a, i) => <span key={i} style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 3, marginRight: 10 }}><Paperclip size={11} /> {a.originalName}</span>)} />}
            <div>
              <span style={{ fontSize: 10.5, color: 'var(--txtm)', textTransform: 'uppercase' }}>Body Preview</span>
              <div style={{ background: '#fff', color: '#333', padding: 18, borderRadius: 'var(--rs)', marginTop: 5, maxHeight: 250, overflow: 'auto', fontSize: 14, lineHeight: 1.7 }}
                dangerouslySetInnerHTML={{ __html: tpl?.body || '' }} />
            </div>
          </div>
        </div>
      )}

      {/* EMAILS TAB */}
      {tab === 'emails' && (<>
        <div style={{ display: 'flex', gap: 5, marginBottom: 10, flexWrap: 'wrap' }}>
          {['', 'pending', 'sent', 'delivered', 'failed', 'bounced', 'replied'].map(st => (
            <button key={st} className={`btn btn-sm ${filter === st ? 'btn-p' : 'btn-g'}`}
              onClick={() => setFilter(st)}>{st || 'All'}</button>
          ))}
        </div>
        <div className="card">
          {emails.length === 0 ? (
            <div className="empty"><MailOpen size={40} /><h3>No emails</h3><p>{s.total === 0 ? 'Launch the campaign to start sending' : 'No emails match this filter'}</p></div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead><tr><th>Recipient</th><th>Name</th><th>Company</th><th>Status</th><th>Sent At</th><th>SMTP Response</th><th>Actions</th></tr></thead>
                <tbody>{emails.map(e => (
                  <tr key={e._id}>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>{e.to}</td>
                    <td>{e.toName || '—'}</td>
                    <td>{e.toCompany || '—'}</td>
                    <td><span className={`badge b-${e.status}`}>{e.status}</span></td>
                    <td style={{ fontSize: 11 }}>{e.sentAt ? new Date(e.sentAt).toLocaleString() : '—'}</td>
                    <td style={{ fontSize: 10.5, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.smtpResponse || e.error || ''}>
                      {e.error ? <span style={{ color: 'var(--err)' }}>{e.error}</span> : (e.smtpResponse || '—')}
                    </td>
                    <td>
                      <button className="btn btn-g btn-sm" onClick={() => openChat(e._id)} title="Chat"><MessageSquare size={13} /></button>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      </>)}

      {/* REPLIES TAB */}
      {tab === 'replies' && (
        <div className="card">
          <div className="card-hdr">
            <span className="card-title">Conversations ({emails.filter(e => e.status === 'replied').length})</span>
            <button className="btn btn-s btn-sm" onClick={handleCheckReplies} disabled={checking}>
              <MailCheck size={13} /> {checking ? 'Checking...' : 'Refresh from IMAP'}
            </button>
          </div>
          {emails.filter(e => e.status === 'replied').length === 0 ? (
            <div className="empty">
              <MessageSquare size={40} /><h3>No replies yet</h3>
              <p>Click "Check Replies (IMAP)" to fetch real replies from your inbox</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 6 }}>
              {emails.filter(e => e.status === 'replied').map(e => (
                <div key={e._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--bgi)', borderRadius: 'var(--rs)', cursor: 'pointer' }} onClick={() => openChat(e._id)}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{e.toName || e.to}</div>
                    <div style={{ fontSize: 11, color: 'var(--txtm)' }}>{e.to} {e.toCompany ? `• ${e.toCompany}` : ''}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="badge b-replied">Replied</span>
                    <span style={{ fontSize: 10.5, color: 'var(--txtm)' }}>{e.repliedAt ? new Date(e.repliedAt).toLocaleString() : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>

    {/* CHAT MODAL */}
    {chatModal && (
      <div className="modal-bg" onClick={() => setChatModal(null)}>
        <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
          <div className="modal-hdr">
            <div>
              <h3>Chat with {chatData.emailLog?.toName || chatData.emailLog?.to}</h3>
              <span style={{ fontSize: 11.5, color: 'var(--txtm)' }}>{chatData.emailLog?.to} {chatData.emailLog?.toCompany ? `• ${chatData.emailLog.toCompany}` : ''}</span>
            </div>
            <button className="btn btn-g btn-sm" onClick={() => setChatModal(null)}><X size={15} /></button>
          </div>
          <div className="chat-box">
            <div className="chat-msgs">
              {/* Original sent email */}
              <div className="chat-bubble chat-out">
                <div style={{ fontSize: 10.5, fontWeight: 600, marginBottom: 3, opacity: .7 }}>Original Email — {chatData.emailLog?.subject}</div>
                <div dangerouslySetInnerHTML={{ __html: tpl?.body?.replace(/\{\{name\}\}/gi, chatData.emailLog?.toName || 'there').replace(/\{\{company\}\}/gi, chatData.emailLog?.toCompany || 'your company').replace(/\{\{email\}\}/gi, chatData.emailLog?.to || '') || '' }} />
                <div className="chat-meta">{chatData.emailLog?.sentAt ? new Date(chatData.emailLog.sentAt).toLocaleString() : 'Not sent'}</div>
              </div>
              {/* Thread */}
              {chatData.replies.map((r, i) => (
                <div key={i} className={`chat-bubble ${r.direction === 'inbound' ? 'chat-in' : 'chat-out'}`}>
                  {r.bodyHtml ? <div dangerouslySetInnerHTML={{ __html: r.bodyHtml }} /> : <p>{r.body}</p>}
                  <div className="chat-meta">{r.direction === 'inbound' ? '← Received' : '→ Sent'} • {new Date(r.createdAt).toLocaleString()}</div>
                </div>
              ))}
            </div>
            <div className="chat-input">
              <input className="inp" value={replyText} onChange={e => setReplyText(e.target.value)}
                placeholder="Type your reply..." style={{ flex: 1 }}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendReply(); } }} />
              <button className="btn btn-p" onClick={handleSendReply} disabled={!replyText.trim()}>
                <Send size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
  </>);
}

function SC({ icon, label, val, cls }) {
  return <div className="stat"><div className={`stat-icon ${cls}`}>{icon}</div><div><h3>{val}</h3><p>{label}</p></div></div>;
}

function Row({ label, val }) {
  return <div><span style={{ fontSize: 10.5, color: 'var(--txtm)', textTransform: 'uppercase' }}>{label}</span><div style={{ fontSize: 13, marginTop: 2 }}>{val}</div></div>;
}
